import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MultiSelect from "@/components/atoms/MultiSelect";
import { postAddRole, postRemoveRole } from "@/services/Settings";
import { CurrentCompany, MembershipType, UserRole, UserRoleType } from "@/types";
import UserRoleTypeEnum from "@/constants/enums/UserRoleTypeEnum";
import { ALL_ROLE_TYPES } from "@/utils/roles";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";
import { getApiError } from "@/utils/apiErrors";

interface RoleSelectorProps {
  userId: number;
  currentRoles: UserRoleType[];
  currentCompany: CurrentCompany | undefined;
  /** True when this row is the signed-in user - guards self-demotion. */
  isSelf: boolean;
  disabled?: boolean;
}

/**
 * Multi-role editor for a single team member. Roles are written one at a time, so each
 * pill added or removed fires exactly one request. Rejected changes leave the value untouched.
 */
function RoleSelector({ userId, currentRoles, currentCompany, isSelf, disabled = false }: RoleSelectorProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const roleOptions = useMemo(
    () => ALL_ROLE_TYPES.map((role) => ({ value: role, label: t(`enumerables:userRoles.${role}`) })),
    [t]
  );

  // Mantine drops a show() whose id is already displayed (notifications.store: "if (notification.id
  // && notifications.some(...)) return"), so every distinct message needs its own id - otherwise a
  // server failure landing within the autoClose window of a guard message is silently swallowed.
  const showError = useCallback(
    (message: string, id: string) =>
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), message, id)),
    [t]
  );

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["getCompanyTeam", currentCompany?.companyId] });
    // AuthContext derives every role predicate from user.role[], so refresh it when
    // the member being edited is the signed-in user - otherwise the UI is stale until reload.
    if (isSelf) {
      await queryClient.invalidateQueries({ queryKey: ["user"] });
    }
  }, [queryClient, currentCompany?.companyId, isSelf]);

  const onSuccess = useCallback(
    () =>
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:settings.userRoleUpdated"),
          "user-role-success"
        )
      ),
    [t]
  );

  const onError = useCallback(
    (error: unknown) =>
      // 243: the last deviation manager can't lose the role while deviation confirmation is on.
      getApiError(error).errorCode === 243
        ? showError(t("notifications:settings.lastDeviationManager"), "user-role-last-deviation-manager")
        : showError(t("notifications:settings.userRoleUpdateError"), "user-role-error"),
    [showError, t]
  );

  const { mutateAsync: addRole, isPending: isAdding } = useMutation({
    mutationFn: async (roleType: UserRoleType) => {
      const role: UserRole = {
        companyId: currentCompany?.companyId as number,
        roleType,
        companyName: currentCompany?.companyName as string,
        companyMemberShip: currentCompany?.companyMembershipType as MembershipType,
      };
      await postAddRole(userId, role);
    },
    onSuccess,
    onError,
    onSettled: invalidate,
  });

  const { mutateAsync: removeRole, isPending: isRemoving } = useMutation({
    mutationFn: async (roleType: UserRoleType) => {
      await postRemoveRole(userId, currentCompany?.companyId as number, roleType);
    },
    onSuccess,
    onError,
    onSettled: invalidate,
  });

  const handleChange = useCallback(
    async (value: string[]) => {
      const next = value as UserRoleType[];

      const added = next.filter((role) => !currentRoles.includes(role));
      const removed = currentRoles.filter((role) => !next.includes(role));

      if (added.length === 0 && removed.length === 0) return;

      // A member without any role is an undefined membership - use Delete to remove the person.
      if (next.length === 0) {
        showError(t("settings:roles.cannotRemoveLastRole"), "user-role-blocked-last-role");
        return;
      }

      if (isSelf && removed.includes(UserRoleTypeEnum.ADMIN)) {
        showError(t("settings:roles.cannotRemoveOwnAdmin"), "user-role-blocked-own-admin");
        return;
      }

      // Sequential, never Promise.all: on failure we must know which writes landed.
      try {
        for (const role of removed) {
          await removeRole(role);
        }
        for (const role of added) {
          await addRole(role);
        }
      } catch {
        // notified by the mutation's onError; onSettled re-syncs from the server
      }
    },
    [currentRoles, isSelf, showError, t, removeRole, addRole]
  );

  return (
    <MultiSelect
      // Mantine forwards unknown props to the inner input, so role-select-* is the combobox
      // itself. wrapperProps puts a testid on the root, which is what actually contains the pills.
      data-testid={`role-select-${userId}`}
      wrapperProps={{ "data-testid": `role-cell-${userId}` }}
      data={roleOptions}
      value={currentRoles}
      onChange={handleChange}
      loading={isAdding || isRemoving}
      variant="filled"
      disabled={disabled || isAdding || isRemoving}
      hidePickedOptions={true}
    />
  );
}

export default RoleSelector;
